import { Messages } from '@/components/Messages';
import { OptimizerVersion } from '@/components/OptimizerVersion';
import { CampaignSummary } from '@/dashboards/CampaignOptimization/components/CampaignSummary';
import { ContributionSeasonalitySection } from '@/dashboards/CampaignOptimization/components/ContributionSeasonalitySection';
import { ControlPanel } from '@/dashboards/CampaignOptimization/components/ControlPanel';
import { MediaInvestmentTable } from '@/dashboards/CampaignOptimization/components/MediaInvestmentTable';
import { NaturalDemandSeasonality } from '@/dashboards/CampaignOptimization/components/NaturalDemandSeasonality';
import { useOptimizationSession } from '@/dashboards/CampaignOptimization/hooks/useOptimizationSession';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import {
    Can,
    FEATURE_FLAG_ACTIONS,
    FEATURE_FLAG_SUBJECTS,
} from '@/features/Casl';
import React from 'react';
import { ContributionShare } from './ContributionShare';

export const CampaignOptimizationSection = ({
    onOutOfBoundsChannel,
}: {
    onOutOfBoundsChannel?: (info: {
        channelName: string;
        min: number;
        max: number;
    }) => void;
} = {}) => {
    const { messages, flushMessages } = useOptimizationSession();
    const {
        project: { projectLead },
    } = useDashboardGlobalContext();

    const showMessages = messages.length > 0;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap w-full max-2xl:gap-4">
                {showMessages && (
                    <Messages
                        messages={messages}
                        onDismiss={flushMessages}
                        projectLeadEmail={projectLead.email}
                    />
                )}
                <div className="flex flex-col w-1/2 max-2xl:w-full max-2xl:pr-0 gap-4 pr-2 max-2xl:order-2">
                    <Can
                        I={FEATURE_FLAG_ACTIONS.VIEW}
                        a={
                            FEATURE_FLAG_SUBJECTS.CAMPAIGN_OPTIMIZATION_DASHBOARD_SUMMARY
                        }
                    >
                        <CampaignSummary />
                    </Can>
                    <Can
                        I={FEATURE_FLAG_ACTIONS.VIEW}
                        a={
                            FEATURE_FLAG_SUBJECTS.CAMPAIGN_OPTIMIZATION_DASHBOARD_MEDIA_INVESTMENT_SPLIT
                        }
                    >
                        <MediaInvestmentTable />
                    </Can>
                    <Can
                        I={FEATURE_FLAG_ACTIONS.VIEW}
                        a={
                            FEATURE_FLAG_SUBJECTS.CAMPAIGN_OPTIMIZATION_DASHBOARD_CONTRIBUTION_SHARE
                        }
                    >
                        <ContributionShare />
                    </Can>
                </div>
                {/* Below 2xl the column dissolves (display: contents) so the panel sits above the
                    summary cards and the seasonality chart below them, without mounting the panel twice. */}
                <div className="flex flex-col w-1/2 gap-4 pl-2 max-2xl:contents">
                    <Can
                        I={FEATURE_FLAG_ACTIONS.VIEW}
                        a={
                            FEATURE_FLAG_SUBJECTS.CAMPAIGN_OPTIMIZATION_DASHBOARD_MODIFY_BUDGET
                        }
                    >
                        <div className="max-2xl:order-1 max-2xl:w-full">
                            <ControlPanel onOutOfBoundsChannel={onOutOfBoundsChannel} />
                        </div>
                    </Can>
                    <Can
                        I={FEATURE_FLAG_ACTIONS.VIEW}
                        a={
                            FEATURE_FLAG_SUBJECTS.CAMPAIGN_OPTIMIZATION_DASHBOARD_NATURAL_DEMAND_SEASONALITY
                        }
                    >
                        <div className="max-2xl:order-3 max-2xl:w-full">
                            <NaturalDemandSeasonality />
                        </div>
                    </Can>
                </div>
            </div>

            <Can
                I={FEATURE_FLAG_ACTIONS.VIEW}
                a={
                    FEATURE_FLAG_SUBJECTS.CAMPAIGN_OPTIMIZATION_DASHBOARD_CONTRIBUTION_SEASONALITY_COMBINED_SECTION
                }
            >
                <ContributionSeasonalitySection />
            </Can>
            <OptimizerVersion />
        </div>
    );
};
